import { z } from 'zod'
import { bzOptional } from '../../domain/common-schemas'
import { JobCostCategory } from '../../domain/jobCosting/JobCostCategory'
import type { JobCostingRollupAggregates } from '../../domain/jobCosting/JobCostingRollupAggregates'
import type { JobCostingRollupFilters } from '../../domain/jobCosting/JobCostingRollupFilters'
import type {
  JobCostingRollupRecord,
  JobCostingRollupTag,
  JobCostingRollupTechnician,
} from '../../domain/jobCosting/JobCostingRollupRecord'
import { JobCostingTrackingStatus } from '../../domain/jobCosting/JobCostingTrackingStatus'
import { JobCostingVerificationStatus } from '../../domain/jobCosting/JobCostingVerificationStatus'
import { LaborActivityTypeSchema } from '../../domain/jobCosting/LaborActivityType'

/**
 * Context enum for snapshot requests
 * - job_details: Office Job Costs Tab (standard masking rules)
 * - gp_dashboard: GP Dashboard Expanded View (full redaction when labor costs hidden)
 * - tech_app: Technician App (controlled by company config)
 *
 * Ref: TRD §5.2, PRD REQ-F3-001..017
 */
export const JobCostingSnapshotContextSchema = z.enum(['job_details', 'gp_dashboard', 'tech_app'])
export type JobCostingSnapshotContext = z.infer<typeof JobCostingSnapshotContextSchema>

/**
 * Schema for creating a new job cost item
 */
export const JobCostItemCreateSchema = z
  .object({
    jobGuid: z.string().uuid(),
    costCategory: JobCostCategory,
    name: z.string().min(1),
    unitCostUsc: z.number().int().nonnegative().nullish(),
    quantity: z.number().positive(), // for labor: minutes>0
    miscCategoryGuid: z.string().uuid().nullish(), // only for costCategory === 'misc'
    laborActivityType: LaborActivityTypeSchema.nullish(), // only for costCategory === 'labor'
    laborUserGuid: z.string().uuid().nullish(), // only for costCategory === 'labor'
    sourcePricebookItemGuid: z.string().uuid().nullish(), // pricebook item this cost was derived from
    context: JobCostingSnapshotContextSchema.nullish(),
  })
  .superRefine((data, ctx) => {
    const isLabor = data.costCategory === 'labor'
    const hasUnitCost = typeof data.unitCostUsc === 'number'

    // Enforce: laborActivityType is required for labor items
    if (isLabor && !data.laborActivityType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'laborActivityType is required for labor items',
        path: ['laborActivityType'],
      })
    }
    // Enforce: laborUserGuid is required for labor items
    if (isLabor && !data.laborUserGuid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'laborUserGuid is required for labor items',
        path: ['laborUserGuid'],
      })
    }
    // Enforce: laborActivityType must be null/undefined for non-labor items
    if (!isLabor && data.laborActivityType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'laborActivityType is only valid for labor items',
        path: ['laborActivityType'],
      })
    }
    // Enforce: laborUserGuid must be null/undefined for non-labor items
    if (!isLabor && data.laborUserGuid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'laborUserGuid is only valid for labor items',
        path: ['laborUserGuid'],
      })
    }
    // Enforce: unitCostUsc is required for non-labor items
    if (!isLabor && !hasUnitCost) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'unitCostUsc is required for non-labor items',
        path: ['unitCostUsc'],
      })
    }
  })
export type JobCostItemCreateInput = z.infer<typeof JobCostItemCreateSchema>

/**
 * Schema for updating an existing job cost item
 */
export const JobCostItemUpdateSchema = z.object({
  jobCostItemGuid: z.string().uuid(),
  context: JobCostingSnapshotContextSchema.nullish(),
  patch: z
    .object({
      name: z.string().min(1).optional(),
      unitCostUsc: z.number().int().nonnegative().optional(),
      quantity: z.number().positive().optional(),
      costCategory: JobCostCategory.optional(),
      miscCategoryGuid: z.string().uuid().nullish(), // only for costCategory === 'misc'
      laborActivityType: LaborActivityTypeSchema.nullish(), // only for costCategory === 'labor'
    })
    .superRefine((patch, ctx) => {
      // Cross-field validation is only possible when the patch explicitly includes costCategory.
      // When costCategory is omitted, we cannot validate against the existing value here.
      if (!('costCategory' in patch)) {
        return
      }

      // Enforce: laborActivityType must be null/undefined when explicitly setting a non-labor category
      if (patch.costCategory !== 'labor' && patch.laborActivityType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'laborActivityType is only valid for labor items',
          path: ['laborActivityType'],
        })
      }

      // Enforce: miscCategoryGuid must be null/undefined when explicitly setting a non-misc category
      if (patch.costCategory !== 'misc' && patch.miscCategoryGuid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'miscCategoryGuid is only valid for misc items',
          path: ['miscCategoryGuid'],
        })
      }
    })
    .refine(p => Object.keys(p).length > 0, 'No-op update'),
})
export type JobCostItemUpdateInput = z.infer<typeof JobCostItemUpdateSchema>

/**
 * Schema for job costing snapshot response
 */
export const JobCostingSnapshotSchema = z.object({
  items: z.array(
    z.object({
      jobCostItemGuid: z.string(),
      companyGuid: z.string(),
      jobGuid: z.string(),
      costCategory: JobCostCategory,
      name: z.string(),
      unitCostUsc: z.number(),
      quantity: z.number(),
      createdByUserGuid: z.string(),
      lastEditedByUserGuid: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      sourceType: z.enum(['manual', 'timesheet', 'invoice']),
      itemTotalUsc: bzOptional(z.number()),
      sourceInvoiceGuid: bzOptional(z.string().uuid()),
      sourceInvoiceDisplayId: bzOptional(z.string()),
      sourceInvoiceSeq: bzOptional(z.number().int()),
      miscCategoryGuid: bzOptional(z.string().uuid().nullable()),
      sourceTimesheetEntryGuid: bzOptional(z.string().uuid()),
      sourceTimesheetUserGuid: bzOptional(z.string().uuid()),
      sourceTimesheetTechnicianName: bzOptional(z.string()),
      sourceTimesheetEntryStartTime: bzOptional(z.string()),
      sourceTimesheetActivityName: bzOptional(z.string().nullable()),
      isLaborEntryInProgress: bzOptional(z.boolean()),
      hasWarning: bzOptional(z.boolean()),
      laborActivityType: bzOptional(LaborActivityTypeSchema.nullable()),
      sourcePricebookItemGuid: bzOptional(z.string().uuid().nullable()),
    }),
  ),
  totals: z.object({
    laborUsc: z.number().int(),
    partsUsc: z.number().int(),
    equipmentUsc: z.number().int(),
    miscUsc: z.number().int(),
    totalCogsUsc: z.number().int(),
  }),
  revenue: z.object({
    source: z.enum(['fully_paid_invoices_v2', 'fully_paid_invoices', 'invoices_sum', 'none']),
    totalRevenueUsc: z.number().int().nonnegative(),
  }),
  grossProfitUsc: z.number().int(),
  grossMarginPct: z.number(),
  marginColor: z.enum(['green', 'yellow', 'red']),
  visibility: z.object({
    canViewCosts: z.boolean(),
    canViewMargins: z.boolean(),
    canViewLaborCosts: z.boolean(),
  }),
  trackingStatus: JobCostingTrackingStatus,
  verificationStatus: JobCostingVerificationStatus,
})
export type JobCostingSnapshotResponse = z.infer<typeof JobCostingSnapshotSchema>
/**
 * Input schema for job costing snapshot
 * Ref: TRD §5.2
 */
export const JobCostingSnapshotInputSchema = z.object({
  jobGuid: z.string().uuid(),
  context: JobCostingSnapshotContextSchema.nullish(),
})
export type JobCostingSnapshotInput = z.infer<typeof JobCostingSnapshotInputSchema>

// Input/output for explicit population endpoints
export const JobCostingPopulateInputSchema = z.object({
  jobGuid: z.string().uuid(),
})

export const JobCostingPopulateResultSchema = z.object({
  createdCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
})

// Filters for rollup reporting (match domain)
export const JobCostingRollupFiltersSchema = z.object({
  dateRange: z.object({
    // ISO YYYY-MM-DD
    startDate: z.string(),
    endDate: z.string(),
  }),
  businessUnitGuids: z.array(z.string().uuid()).optional(),
  technicianUserGuids: z.array(z.string().uuid()).optional(),
  jobTagGuids: z.array(z.string().uuid()).optional(),
  accountTagGuids: z.array(z.string().uuid()).optional(),
  accountGuids: z.array(z.string().uuid()).optional(),
  jobTypeGuids: z.array(z.string().uuid()).optional(),
  verifiedOnly: z.boolean().optional(),
}) satisfies z.ZodType<JobCostingRollupFilters>

// Technician sub-record
export const JobCostingRollupTechnicianSchema = z.object({
  userGuid: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
}) satisfies z.ZodType<JobCostingRollupTechnician>

// Tag sub-record
export const JobCostingRollupTagSchema = z.object({
  tagGuid: z.string().uuid(),
  name: z.string(),
}) satisfies z.ZodType<JobCostingRollupTag>

// Main rollup record
export const JobCostingRollupRecordSchema = z.object({
  jobGuid: z.string().uuid(),
  reportDateIso: z.string(),
  reportLocalTimestamp: z.string(),
  reportLocalDate: z.string(),
  jobTypeGuid: z.string().uuid(),
  jobTypeName: z.string(),
  jobClass: z.string().nullable(),
  revenueUsc: z.number().int(),
  laborCostsUsc: z.number().int(),
  partsCostsUsc: z.number().int(),
  equipmentCostsUsc: z.number().int(),
  miscCostsUsc: z.number().int(),
  totalCostsUsc: z.number().int(),
  grossProfitUsc: z.number().int(),
  grossProfitMarginRate: z.number().nullable(),
  businessUnitGuid: z.string().uuid().nullable(),
  companyGuid: z.string().uuid(),
  assignedTechnicians: z.array(JobCostingRollupTechnicianSchema),
  assignedTechnicianGuids: z.array(z.string().uuid()),
  jobTags: z.array(JobCostingRollupTagSchema),
  jobTagGuids: z.array(z.string().uuid()),
  accountTags: z.array(JobCostingRollupTagSchema),
  accountTagGuids: z.array(z.string().uuid()),
  accountGuid: z.string().uuid(),
  accountType: z.string(),
  locationGuid: z.string().uuid().nullable(),
  zipCode: z.string().nullable(),
  jobCompanyLeadSourceGuid: z.string().uuid().nullable(),
  jobCompanyLeadSourceName: z.string().nullable(),
  accountCompanyLeadSourceGuid: z.string().uuid().nullable(),
  accountCompanyLeadSourceName: z.string().nullable(),
  jobCostingVerificationStatus: JobCostingVerificationStatus,
}) satisfies z.ZodType<JobCostingRollupRecord>

// Aggregates for rollup reporting
export const JobCostingRollupAggregatesSchema = z.object({
  revenueUsc: z.number().int(),
  laborCostsUsc: z.number().int(),
  partsCostsUsc: z.number().int(),
  equipmentCostsUsc: z.number().int(),
  miscCostsUsc: z.number().int(),
  totalCostsUsc: z.number().int(),
  grossProfitUsc: z.number().int(),
  grossMarginPct: z.number(),
  count: z.number().int(),
}) satisfies z.ZodType<JobCostingRollupAggregates>

// Capacity entry for man-days calculation
export const CapacityEntrySchema = z.object({
  userGuid: z.string().uuid(),
  startTime: z.string(), // ISO 8601 timestamp
  endTime: z.string(), // ISO 8601 timestamp
  localDateTimeStart: z.string(), // Localized datetime for display
  localDateTimeEnd: z.string(), // Localized datetime for display
  durationMinutes: z.number().int().nonnegative(), // Computed duration
  localDate: z.string(), // YYYY-MM-DD format for grouping
  businessUnit: z
    .object({
      businessUnitGuid: z.string().uuid(),
      name: z.string(),
    })
    .nullable(), // User's default BU or job's BU override; null if no default BU
})
export type CapacityEntry = z.infer<typeof CapacityEntrySchema>

// Business unit target metric for charting target lines
export const BusinessUnitTargetMetricSchema = z.object({
  businessUnitGuid: z.string().uuid(),
  businessUnitName: z.string(),
  metricName: z.string(), // e.g., 'gp_per_man_day_target' or 'gp_per_man_day_goal'
  metricValue: z.number(), // Value in USC (US Cents)
  dateStart: z.string(), // YYYY-MM-DD
  dateEnd: z.string().nullable(), // YYYY-MM-DD or null for current/ongoing
})
export type BusinessUnitTargetMetric = z.infer<typeof BusinessUnitTargetMetricSchema>

// Request with pagination and sort defaults/clamps
export const JobCostingRollupRequestSchema = z.object({
  filter: JobCostingRollupFiltersSchema,
  pagination: z
    .object({
      limit: z.number().int().min(1).max(1000).default(100),
      offset: z.number().int().min(0).default(0),
    })
    .default({
      limit: 100,
      offset: 0,
    }),
  sort: z
    .object({
      field: z.enum(['reportLocalDate', 'grossProfitUsc', 'revenueUsc']).default('reportLocalDate'),
      direction: z.enum(['asc', 'desc']).default('desc'),
    })
    .default({
      field: 'reportLocalDate',
      direction: 'desc',
    }),
})

// Response payload
export const JobCostingRollupResponseSchema = z.object({
  rows: z.array(JobCostingRollupRecordSchema),
  aggregates: JobCostingRollupAggregatesSchema,
  capacity: z.array(CapacityEntrySchema),
  businessUnitTargetMetrics: z.array(BusinessUnitTargetMetricSchema),
})

// Type aliases (DTOs)
export type JobCostingRollupFiltersDto = z.infer<typeof JobCostingRollupFiltersSchema>
export type JobCostingRollupTechnicianDto = z.infer<typeof JobCostingRollupTechnicianSchema>
export type JobCostingRollupTagDto = z.infer<typeof JobCostingRollupTagSchema>
export type JobCostingRollupRecordDto = z.infer<typeof JobCostingRollupRecordSchema>
export type JobCostingRollupAggregatesDto = z.infer<typeof JobCostingRollupAggregatesSchema>
export type JobCostingRollupRequestDto = z.infer<typeof JobCostingRollupRequestSchema>
export type JobCostingRollupResponseDto = z.infer<typeof JobCostingRollupResponseSchema>

/**
 * Input schema for updating job costing tracking status
 */
export const UpdateJobCostingTrackingStatusInputSchema = z.object({
  jobGuid: z.string().uuid(),
  trackingStatus: JobCostingTrackingStatus,
})
export type UpdateJobCostingTrackingStatusInput = z.infer<typeof UpdateJobCostingTrackingStatusInputSchema>

/**
 * Input schema for updating job costing verification status
 */
export const UpdateJobCostingVerificationStatusInputSchema = z.object({
  jobGuid: z.string().uuid(),
  verificationStatus: JobCostingVerificationStatus,
})
export type UpdateJobCostingVerificationStatusInput = z.infer<typeof UpdateJobCostingVerificationStatusInputSchema>

/**
 * Response schema for job costing status operations
 */
export const JobCostingStatusResponseSchema = z.object({
  jobGuid: z.string().uuid(),
  trackingStatus: JobCostingTrackingStatus,
  verificationStatus: JobCostingVerificationStatus,
  updatedAt: z.string(),
})
export type JobCostingStatusResponse = z.infer<typeof JobCostingStatusResponseSchema>
